import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';

import { ExpenseService } from '../../../../core/services/expense.service';
import { ClientService } from '../../../../core/services/client.service';
import { CaseService } from '../../../../core/services/case.service';
import { DiaryService } from '../../../../core/services/diary.service';
import { ExpenseForCreation } from '../../../../core/models/expenses/expense-for-creation.model';
import { ExpenseForUpdation } from '../../../../core/models/expenses/expense-for-updation.model';
import { atLeastOneValidator } from '../../../../core/validators/at-least-one-validator';

@Component({
  selector: 'app-expense-form',
  standalone: false,
  templateUrl: './expense-form.component.html',
  styleUrl: './expense-form.component.css'
})

export class ExpenseFormComponent implements OnInit {
  expenseForm!: FormGroup;
  isEditMode = false;
  isSubmited = false;
  expenseId!: number;
  clientId: number | null = null;
  clients: any[] = [];
  cases: any[] = [];
  diaries: any[] = [];
  private returnUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private expenseService: ExpenseService,
    private clientService: ClientService,
    private caseService: CaseService,
    private diaryService: DiaryService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    // Check route params synchronously first to determine edit mode
    const routeParams = this.route.snapshot.params;
    if (routeParams['id']) {
      this.isEditMode = true;
      this.expenseId = +routeParams['id'];
    }

    this.buildForm();

    // Subscribe to route params for loading data
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.expenseId = +params['id'];
        this.loadExpenseData(this.expenseId);
      }
    });
    this.loadClients();
    this.loadCases();
    this.loadDiaries();

    // Get returnUrl from router state, history state, or query params
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['returnUrl']) {
      this.returnUrl = navigation.extras.state['returnUrl'];
    } else if ((window.history.state as any)?.returnUrl) {
      this.returnUrl = (window.history.state as any).returnUrl;
    } else {
      this.route.queryParams.subscribe(params => {
        if (params['returnUrl']) {
          this.returnUrl = params['returnUrl'];
        }
      });
    }

    // Read clientId from query params (regardless of returnUrl)
    // First check synchronously from snapshot
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['clientId']) {
      this.clientId = +queryParams['clientId']; // convert to number
      this.expenseForm.patchValue({ clientId: this.clientId });
    }
    
    // Also subscribe for any changes
    this.route.queryParams.subscribe(params => {
      if (params['clientId']) {
        this.clientId = +params['clientId']; // convert to number
        this.expenseForm.patchValue({ clientId: this.clientId });
      }
    });
  }

  private navigateBack(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      // If we have a clientId, try to go to client details, otherwise use history or default
      if (this.clientId) {
        this.router.navigate(['/clients/details', this.clientId], {
          queryParams: { activeTab: 'expenses' }
        });
      } else if (window.history.length > 1) {
        this.location.back();
      } else {
        this.router.navigate(['/expenses']);
      }
    }
  }

  private buildForm(): void {
    // Set current date as default when creating (not editing)
    const defaultDate = this.isEditMode ? null : new Date();
    
    this.expenseForm = this.fb.group({
      expenseType: [''],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      description: [''],
      expenseDate: [defaultDate],
      caseId: [null],
      clientId: [null],
      diaryId: [null]
    }, {
      validators: atLeastOneValidator(['clientId', 'caseId', 'diaryId'])
    });
  }

  /**
   * Check if the form has the "at least one required" validation error
   */
  hasAtLeastOneError(): boolean {
    return this.expenseForm.hasError('atLeastOneRequired') && 
           (this.expenseForm.touched || this.isSubmited);
  }

  private loadClients(): void {
    this.clientService.getPaginatedClients({ page: 1, pageSize: 100000 }).subscribe({
      next: res => this.clients = res.data,
      error: () => this.snackBar.open('Failed to load clients', 'Close', { duration: 3000 })
    });
  }

  private loadCases(): void {
    this.caseService.getPaginatedCases({ page: 1, pageSize: 100000 }).subscribe({
      next: res => this.cases = res.data,
      error: () => this.snackBar.open('Failed to load cases', 'Close', { duration: 3000 })
    });
  }

  private loadDiaries(): void {
    this.diaryService.getPaginatedDiaries({ page: 1, pageSize: 100000 }).subscribe({
      next: res => this.diaries = res.data,
      error: () => this.snackBar.open('Failed to load diaries', 'Close', { duration: 3000 })
    });
  }

  private loadExpenseData(id: number): void {
    this.expenseService.getExpenseById(id).subscribe({
      next: expenseData => this.expenseForm.patchValue(expenseData),
      error: () => this.snackBar.open('Failed to load expense details', 'Close', { duration: 3000 })
    });
  }

  submitForm(): void {
    // Mark all fields as touched to show validation errors
    Object.keys(this.expenseForm.controls).forEach(key => {
      this.expenseForm.get(key)?.markAsTouched();
    });

    if (this.expenseForm.invalid){
      // Keep button enabled if validation fails so user can fix and retry
      this.isSubmited = false;
      return;
    }

    // Only disable button when validation passes and we're submitting
    this.isSubmited = true;

    if (this.isEditMode) {
      const updatedExpense: ExpenseForUpdation = {
        id: this.expenseId,
        ...this.expenseForm.value
      };

      this.expenseService.updateExpense(updatedExpense).subscribe({
        next: () => {
          this.snackBar.open('Expense updated successfully', 'Close', { duration: 3000 });
          this.navigateBack();
        },
        error: () => {
          this.snackBar.open('Failed to update expense', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });
    } else {
      const newExpense: ExpenseForCreation = this.expenseForm.value;

      this.expenseService.createExpense(newExpense).subscribe({
        next: () => {
          this.snackBar.open('Expense added successfully', 'Close', { duration: 3000 });
          this.navigateBack();
        },
        error: () => {
          this.snackBar.open('Failed to add expense', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });
    }
  }
}
